enum cartStatus {
  'COMPLETE',
  'ACTIVE',
  'INACTIVE',
}

enum DiscountCodeApplicationInfoStatus {
  'VALID',
  'INVALID',
  'INELIGIBLE',
}

interface DiscountCodeApplicationInfo {
  status: DiscountCodeApplicationInfoStatus
  discountAmount: number
}

interface CartCostSummary {
  discountCodeApplicationInfo: DiscountCodeApplicationInfo
  subTotalAmount: number
  totalDiscount: number
  totalStoreCreditGrant: number
  totalAmount: number
}

// Subscription-related types
export enum PurchaseMode {
  ONE_TIME = 'ONE_TIME',
  RECURRING = 'RECURRING',
}

export interface SubscriptionDetail {
  customerSubscriptionId: number | null
  subscriptionPlanId: number
  maxQuantityAllowed: number
}

export interface SubscriptionFrequency {
  id: number
  frequencyCode: string | null
  unit: string
  interval: number
  displayLabel: string
}

export interface FrequencyOption {
  planId: number
  label: string
  discountPercent: number
  isDefault?: boolean
}

export interface SubscriptionPlan {
  id: number
  frequency: SubscriptionFrequency
  isDefault: boolean
  maxAllowedQuantity: number
  benefitMode: string
  benefitValue: number
  benefitValueType: string
  updatedBy: string
  updatedAt: string
}

export interface Cart {
  id: string
  customerId?: string
  lineItems: CartLineItem[]
  saveForLaterItems?: CartLineItem[]
  outOfStockLineItems?: CartLineItem[]
  currencyCode: string
  discountCode?: string
  cartStatus: cartStatus
  cartCostSummary?: CartCostSummary
  createdAt: string
  updatedAt: string
  giftingDetails?: GiftingDetails
  isGift?: boolean
  isDateOfBirthInformationAvailable?: boolean
  isDateOfBirthRequired?: boolean
  isAgeRestrictedCart?: boolean
}

export interface CartAttribute {
  key: string
  value: string
}

export enum PromoType {
  STORE_CREDIT_GRANT = 'STORE_CREDIT_GRANT',
  MONEY_DISCOUNT = 'MONEY_DISCOUNT',
  SUBSCRIPTION_DISCOUNT = 'SUBSCRIPTION_DISCOUNT',
  SUBSCRIPTION_STORE_CREDIT = 'SUBSCRIPTION_STORE_CREDIT',
}

export interface PromoAllocation {
  amount: number
  promoCode: string
  promoType: PromoType
  promoId?: number
  promoApplicationType?: 'AUTOMATIC' | 'MANUAL'
  percentageAmount?: number
}
interface CartLineItemCostSummary {
  promoAllocations: PromoAllocation[]
  amountPerQuantity: number
  subtotalAmount?: number
  totalDiscount?: number
  totalStoreCreditGrant?: number
  totalAmount?: number
}

interface VariantMetaData {
  variantId: number
  productId: number
  price: number
  imageUrl: string
  productTitle: string
  variantTitle: string
  sku?: string
  // Subscription-related fields
  isSubscribable?: boolean
  plans?: SubscriptionPlan[]
  subscriptionMaxQuantity?: number // Max quantity for subscription items (e.g., 10)
}

export interface CartLineItem {
  id: string
  variantId: number
  quantity: number
  attributes: CartAttribute[]
  costSummary?: CartLineItemCostSummary
  variantMetaData?: VariantMetaData
  createdAt: string
  updatedAt: string
  totalLineItemQuantity: number
  productMetaData?: ProductMetaData
  inventoryData?: InventoryData
  inventoryStatus?: 'IN_STOCK' | 'OUT_OF_STOCK' | 'PARTIAL_OUT_OF_STOCK'
  slug?: string
  // Subscription-related fields
  purchaseMode?: PurchaseMode
  subscriptionDetail?: SubscriptionDetail
}

interface ProductMetaData {
  svid: string | number
  spid: string | number
  productId: string | number
  options: {
    name: string
    value: string
  }[]
}

interface InventoryData {
  available: number
  tags: {
    preOrderShippingDate: string
  }
}

export interface LineItem {
  variantId: number | string
  quantity: number
  attributes: any[]
  subscriptionPlanId?: number
}

export type LineItems = LineItem[]

export interface TBuyNowApiResponse {
  buyNowCartAuthMapping: {
    id: string
    authCode: string
  }
  parentCartAuthMapping: {
    id: string
    authCode: string
  }
  buyNowCartCheckoutMapping: {
    cartId: string
    checkoutId: string
    checkoutAuthCode: string
  }
}

export interface GiftingDetails {
  recipientName: string
  recipientEmail: string
  senderName: string
  giftMessage: string
}
